import { CATEGORY_MANAGEMENT, FILTER_FEATURE } from "api/ApiPath";
import { CategoryAddtionRequestModel } from "api/repositories/packagesManagement/topupManagement/model/categoryAddtion/CategoryAddtionRequestModel";
import { CategoryAddtionResponseModel } from "api/repositories/packagesManagement/topupManagement/model/categoryAddtion/CategoryAddtionResponseModel";
import { TopupCreateItemRequestModel } from "api/repositories/packagesManagement/topupManagement/model/create/TopupCreateItemRequestModel";
import { TopupCreateRequestModel } from "api/repositories/packagesManagement/topupManagement/model/create/TopupCreateRequestModel";
import { TopupPackageDetailModel } from "api/repositories/packagesManagement/topupManagement/model/detail/TopupDetailReponseModel";
import { SupplierAddtionResponseModel } from "api/repositories/packagesManagement/topupManagement/model/supplierAddition/SupllierAddtionResponseModel";
import { SupplierAddtionRequestModel } from "api/repositories/packagesManagement/topupManagement/model/supplierAddition/SupplierAddtionRequestModel";
import { TopupRequestModel } from "api/repositories/packagesManagement/topupManagement/model/TopupRequestModel";
import { TopupResponseModel } from "api/repositories/packagesManagement/topupManagement/model/TopupResponseModel";
import { TopupUpdateRequestModel } from "api/repositories/packagesManagement/topupManagement/model/update/TopupUpdateRequestModel";
import { RoamingPartnerRequestModel } from "api/repositories/roamingPartnerManagement/model/RoamingPartnerRequestModel";
import { RoamingPartnerResponseModel } from "api/repositories/roamingPartnerManagement/model/RoamingPartnerResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseCategoryRequestModel } from "app/based/baseApiModel/baseCategory/BaseCategoryRequestModel";
import { BaseCategoryResponseModel } from "app/based/baseApiModel/baseCategory/BaseCategoryResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { TempCarrierId } from "features/airTimeManagement/presentation/constants/AirTimeManagementConstants";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { MobileDataService } from "utils/enums/MobileDataEnums";
import { FeatureFormat } from "utils/format/FeatureFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";

type SupplierFilterRequestModel = BaseFilterListRequestModel & {
  pagination?: boolean;
};

type SupplierFilterModel = BaseFilterListResponseModel & {
  displayName?: string;
  order?: number;
};

class PackageManagementInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public modalLoading = new BehaviorSubject<boolean>(false);
  public fetchPackageList = new BehaviorSubject<TopupRequestModel>(
    new TopupRequestModel(),
  );
  public fetchCountry = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchSupplierList = new BehaviorSubject<SupplierFilterRequestModel>(
    {},
  );
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchRoamingPartnerList =
    new BehaviorSubject<RoamingPartnerRequestModel>({});
  public fetchAddition = new BehaviorSubject<{
    type?: "category" | "supplier";
    body?: CategoryAddtionRequestModel | SupplierAddtionRequestModel;
  }>({});
  public fetchDetail = new BehaviorSubject<{ code?: string }>({});
  public fetchCategory = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    body?: TopupUpdateRequestModel | TopupCreateItemRequestModel;
    action?: "update" | "create";
    service?: MobileDataService;
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.fetchPackageList.complete();
    this.fetchCountry.complete();
    this.fetchSupplierList.complete();
    this.modalLoading.complete();
    this.fetchCarrier.complete();
    this.fetchRoamingPartnerList.complete();
    this.fetchAddition.complete();
    this.fetchActions.complete();
    this.fetchDetail.complete();
    this.fetchCategory.complete();
  }
}

class PackageManagementOutput implements Disposable {
  public savedPaging = new BehaviorSubject<Paging>({});
  public savedCategory = new BehaviorSubject<Array<BaseCategoryResponseModel>>(
    [],
  );
  public packageList = new BehaviorSubject<Array<TopupResponseModel>>([]);
  public savedDetail = new BehaviorSubject<TopupPackageDetailModel>({});
  public countryList = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public supplierList = new BehaviorSubject<Array<SupplierFilterModel>>([]);
  public addtionCategoryList = new BehaviorSubject<
    Array<CategoryAddtionResponseModel>
  >([]);
  public addtionSupplierList = new BehaviorSubject<
    Array<SupplierAddtionResponseModel>
  >([]);
  public roamingPartnerList = new BehaviorSubject<
    Array<RoamingPartnerResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public onError = new BehaviorSubject<Error>({});
  public createUpdateSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedPaging.complete();
    this.packageList.complete();
    this.savedCategory.complete();
    this.savedDetail.complete();
    this.countryList.complete();
    this.supplierList.complete();
    this.savedCarrier.complete();
    this.onError.complete();
    this.addtionCategoryList.complete();
    this.addtionSupplierList.complete();
    this.roamingPartnerList.complete();
    this.createUpdateSuccess.complete();
  }
}

@injectable()
export class PackageManagementViewModel extends BaseViewModel<
  PackageManagementInput,
  PackageManagementOutput
> {
  constructor() {
    super(new PackageManagementInput(), new PackageManagementOutput());
  }
  private packageRepo = ServiceRepo.getPackageManagementRepo();
  private supplierRepo = ServiceRepo.getSupplierRepo();
  private roamingPartnerRepo = ServiceRepo.getRoamingPartnerRepo();
  private filterUseCase = FilterInjection.getUseCase();

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchPackageList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.packageRepo.getList(params);
        if (res?.data) {
          this.output.packageList.next(res?.data);
          this.output.savedPaging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.packageList.next([]);
        this.output.savedPaging.next({ page: 0, limit: 10, total: 0 });
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.packageRepo.detailTopup(params?.code);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCategory, async (params) => {
      return await FeatureFormat.fetchFilterList<
        BaseCategoryResponseModel,
        BaseCategoryRequestModel
      >({
        dataState: (value) => this.output.savedCategory.next(value),
        path: CATEGORY_MANAGEMENT.FILTER_LIST,
        isForceRefresh: false,
        isNeedFeature: false,
        savedKey: CacheKeyEnum.categoryListForPackage,
        params,
      });
    });

    this.skipFirstInitial(this.input.fetchCountry, async (params) => {
      try {
        const res = await this.filterUseCase.fetchListWithFeature({
          path: FILTER_FEATURE.NATION_LIST,
          params,
          isNeedFeature: false,
        });
        if (res) {
          this.output.countryList.next(res);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchSupplierList, async (params) => {
      try {
        const res = await this.supplierRepo.getList(params);
        if (res?.data) {
          this.output.supplierList.next(res?.data);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(
      this.input.fetchRoamingPartnerList,
      async (params) => {
        try {
          const res = await this.roamingPartnerRepo.getList(params);
          if (res?.data) {
            this.output.roamingPartnerList.next(res?.data);
          }
        } catch (error) {
          console.error(error);
        }
      },
    );

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        await FeatureFormat.fetchFilterList<
          BaseFilterListResponseModel,
          BaseFilterListRequestModel
        >({
          dataState: (value) =>
            this.output.savedCarrier.next(
              value
                ?.filter(
                  (item) =>
                    item?.code === "viettel" ||
                    item?.code === "mobifone" ||
                    item?.code === "vinaphone",
                )
                ?.map((child) => ({
                  id: TempCarrierId[child?.code],
                  ...child,
                })),
            ),
          path: FILTER_FEATURE.CARRIER_LIST,
          savedKey: CacheKeyEnum.carrierForPackage,
          isNeedFeature: false,
          isForceRefresh: true,
          params,
        });
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchAddition, async (query) => {
      let { body, type } = query;
      try {
        let res = undefined;
        switch (type) {
          case "category":
            res = await this.packageRepo.addCategory(body);
            if (res) {
              this.output.addtionCategoryList.next(res?.data);
            }
            break;
          case "supplier":
            res = await this.packageRepo.addSupplier(body);
            if (res) {
              this.output.addtionSupplierList.next(res?.data);
            }
            break;
        }
      } catch (error) {
        this.output.onError.next(error);
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body, service } = props;
      switch (action) {
        case "update":
          try {
            this.input.modalLoading.next(true);
            delete body["capacityUnit"];
            delete body["durationUnit"];
            const res = await this.packageRepo?.updateTopup(
              body as TopupUpdateRequestModel,
            );
            if (res?.data) {
              getMessage(
                strings.GlobalMessage.UpdateSuccessfully,
                5,
                "success",
              );
              this.output.createUpdateSuccess.next(true);
            } else {
              getMessage(
                `${strings.GlobalMessage.UpdateFailed} ${res?.message}`,
                5,
                "error",
              );
            }
          } catch (error: any) {
            console.error(error);
            getMessage(
              `${strings.GlobalMessage.UpdateFailed} ${error?.message}`,
              5,
              "error",
            );
          } finally {
            this.input.modalLoading.next(true);
          }
          break;
        case "create":
          try {
            this.input.modalLoading.next(true);
            delete body["capacityUnit"];
            delete body["durationUnit"];
            let serviceData = new TopupCreateRequestModel(
              service as MobileDataService,
              body,
            );
            const res = await this.packageRepo?.createTopup(serviceData);
            if (res?.data) {
              getMessage(
                strings.GlobalMessage.CreateSuccessfully,
                5,
                "success",
              );
              this.output.createUpdateSuccess.next(true);
            } else {
              getMessage(
                `${strings.GlobalMessage.CreateFailed} ${res?.message}`,
                5,
                "error",
              );
            }
          } catch (error: any) {
            console.error(error);
            getMessage(
              `${strings.GlobalMessage.CreateFailed} ${error?.message}`,
              5,
              "error",
            );
          } finally {
            this.input.modalLoading.next(false);
          }
          break;
      }
    });
  }
}
